/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardFooter,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import z from 'zod';

/**
 * Internal dependencies
 */
import Tiptap from '@Components/TipTap';
import Breadcrumbs from '@Components/Breadcrumbs';
import { useQueryClient } from '@tanstack/react-query';
import { useVenues } from '../../hooks/useVenues';
import service from '../../service';
import { CreateSpacePayload } from '@Types/api';
import { cn } from '@Utils/cn';
import { Check, ChevronsUpDown } from 'lucide-react';

const FormSchema = z.object({
  name: z.string().min(2, {
    message: 'A name is required.',
  }),
  description: z.string().optional(),
  teaser: z.string().optional().nullable(),
  capacity: z.number().min(1, { message: 'Capacity must be at least 1.' }),
  guestMin: z
    .number()
    .min(1, { message: 'Minimum guests must be at least 1.' }),
  tripleSeatId: z.number().int().positive().nullable(),
  venueId: z.string().min(1, { message: 'A venue is required.' }),
  bookingTypes: z.array(z.enum(['PRIVATE_DINING', 'CORPORATE'])),
});

type FormValues = z.infer<typeof FormSchema>;

export default function CreateSpace() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [venuesOpen, setVenuesOpen] = useState(false);

  const { data: venues, status: venuesStatus } = useVenues({
    include: 'spaces',
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      description: '',
      teaser: '',
      capacity: 20,
      guestMin: 10,
      tripleSeatId: null,
      venueId: '',
      bookingTypes: [],
    },
  });

  const onSubmit = async (values: FormValues) => {
    const {
      name,
      description,
      teaser,
      capacity,
      guestMin,
      tripleSeatId,
      venueId,
      bookingTypes,
    } = values;

    const body: CreateSpacePayload = {
      name,
      ...(description && description.trim()
        ? { description: description.trim() }
        : {}),
      capacity,
      guestMin,
      ...(tripleSeatId != null ? { tripleSeatId } : {}),
      venueId,
    };
    if (teaser != null && teaser.trim()) {
      body.teaser = teaser.trim();
    }
    if (bookingTypes && bookingTypes.length > 0) {
      body.bookingTypes = bookingTypes;
    }

    // clear any previous root error before submitting
    form.clearErrors('root');

    try {
      const space = await service.createSpace(body);

      if (!space) {
        // treat undefined / falsy as an error
        throw new Error('No space returned from API');
      }

      await queryClient.invalidateQueries({ queryKey: ['spaces'] });
      toastSuccess('Space created successfully.');
      navigate('/spaces');
    } catch (error) {
      console.error('Error creating space:', error);
      form.setError('root', {
        type: 'server',
        message:
          'We were unable to create this space. Please check your details and try again.',
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Locations', href: '/locations' },
          { name: 'Spaces', href: '/spaces' },
          { name: 'Create', href: '/spaces/create' },
        ]}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mt-8 mb-4">
            <Heading level="h2">Create A Space</Heading>
          </div>
          <Card>
            <CardContent>
              {/* Top-level error banner */}
              {rootError && (
                <div className="mb-4">
                  <Alert variant="destructive">
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription>{rootError}</AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="space-y-6 py-3">
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Space name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Tiptap
                          value={field.value || ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="Describe this space..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Teaser */}
                <FormField
                  control={form.control}
                  name="teaser"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teaser</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Short summary or tagline for this space."
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Capacity */}
                <FormField
                  control={form.control}
                  name="capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Capacity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Guest Min */}
                <FormField
                  control={form.control}
                  name="guestMin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum guests</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Tripleseat Room ID */}
                <FormField
                  control={form.control}
                  name="tripleSeatId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tripleseat Room ID</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Numeric Room ID from Tripleseat used for event bookings.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Booking Types */}
                <FormField
                  control={form.control}
                  name="bookingTypes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Booking Types</FormLabel>
                      <FormDescription>
                        Controls which areas of the members app and events app
                        this space will be displayed in.
                      </FormDescription>
                      <div className="flex flex-col gap-3">
                        {(
                          [
                            {
                              value: 'PRIVATE_DINING',
                              label: 'Private Dining',
                              description:
                                'This space will appear in the private dining section of the events and members apps, when all other conditions are met.',
                            },
                            {
                              value: 'CORPORATE',
                              label: 'Corporate',
                              description:
                                'This space will appear in the corporate events section of the events and members apps, when all other conditions are met.',
                            },
                          ] as const
                        ).map((option) => (
                          <div key={option.value} className="flex gap-2">
                            <Checkbox
                              className="mt-0.5"
                              checked={field.value?.includes(option.value)}
                              onCheckedChange={(checked) => {
                                const current = field.value ?? [];
                                field.onChange(
                                  checked
                                    ? [...current, option.value]
                                    : current.filter((v) => v !== option.value),
                                );
                              }}
                            />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">
                                {option.label}
                              </span>
                              <span className="text-muted-foreground text-xs">
                                {option.description}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Venue */}
                {venuesStatus === 'success' && venues && venues.length > 0 ? (
                  <FormField
                    control={form.control}
                    name="venueId"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Venue</FormLabel>
                        <Popover
                          modal={true}
                          open={venuesOpen}
                          onOpenChange={setVenuesOpen}
                        >
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  'w-full justify-between',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                {field.value
                                  ? venues.find(
                                      (venue) => venue.id === field.value,
                                    )?.name
                                  : 'Select venue'}
                                <ChevronsUpDown className="opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="max-w-full p-0"
                            align="start"
                          >
                            <Command>
                              <CommandInput
                                placeholder="Search venues..."
                                className="h-9"
                              />
                              <CommandList>
                                <CommandEmpty>No venues found</CommandEmpty>
                                <CommandGroup>
                                  {venues.map((venue) => (
                                    <CommandItem
                                      value={venue.name}
                                      key={venue.id}
                                      onSelect={() => {
                                        field.onChange(venue.id);
                                        setVenuesOpen(false);
                                      }}
                                    >
                                      {venue.name}
                                      <Check
                                        className={cn(
                                          'ml-auto',
                                          venue.id === field.value
                                            ? 'opacity-100'
                                            : 'opacity-0',
                                        )}
                                      />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <div>
                    No venues available. You must create a venue before you can
                    create a space.{' '}
                    <Button variant="link" asChild>
                      <Link to="/venues/create">Create a venue</Link>
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex gap-2">
              <Button
                type="submit"
                variant="primary"
                loading={form.formState.isSubmitting}
              >
                Create
              </Button>
              <Button variant="link" asChild>
                <Link to="/spaces">Cancel</Link>
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
